import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { get, post } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import { Button } from '@/components/atoms/Button';
import { usePointStore } from '@/store/pointStore';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { toast } from '@/store/toastStore';

interface CheckinStatus {
  checkedToday: boolean;
  streak: number;
  nextBonus: { days: number; amount: number } | null;
}

interface MonthAttendance {
  month: string;
  days: string[];
  alt_days: string[];
  streak: number;
  checkedToday: boolean;
  alternative_count_this_week: number;
  replaceable_date: string | null;
}

/** 대체 출석 주간 상한 — 백엔드 ALT_CHECKIN_WEEKLY_LIMIT 와 동기화 */
const ALT_WEEKLY_LIMIT = 2;

interface CheckinResult {
  success: boolean;
  alreadyDone?: boolean;
  balance?: number;
  earned?: number;
  bonusEarned?: number;
  streak?: number;
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function toMonthStr(year: number, month0: number) {
  return `${year}-${String(month0 + 1).padStart(2, '0')}`;
}

function toDateStr(year: number, month0: number, day: number) {
  return `${year}-${String(month0 + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function kstToday() {
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return {
    year: kst.getUTCFullYear(),
    month0: kst.getUTCMonth(),
    day: kst.getUTCDate(),
    dateStr: kst.toISOString().slice(0, 10),
  };
}

export function CheckinCard() {
  const queryClient = useQueryClient();
  const addToast = usePointStore((s) => s.addToQueue);
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const setLoginModalOpen = useUIStore((s) => s.setLoginModalOpen);

  const today = kstToday();
  const [year, setYear] = useState(today.year);
  const [month0, setMonth0] = useState(today.month0);
  const monthStr = toMonthStr(year, month0);

  const { data: status } = useQuery({
    queryKey: queryKeys.point.checkinStatus,
    queryFn: () => get<CheckinStatus>('/points/checkin/status'),
    staleTime: 60_000,
  });

  const { data: monthData } = useQuery({
    queryKey: ['attendance', 'me', monthStr],
    queryFn: () => get<MonthAttendance>(`/points/attendance/me?month=${monthStr}`),
    staleTime: 60_000,
  });

  const checkinMutation = useMutation({
    mutationFn: () => post<CheckinResult>('/points/checkin'),
    onSuccess: (result) => {
      if (result.alreadyDone) {
        addToast(0, '이미 출석 완료');
        return;
      }
      const earned = result.earned ?? 5;
      addToast(earned, `${result.streak}일 연속 출석`);
      toast.success(`출석체크 완료! +${earned}P 지급됐어요 🎉`);
      if (result.bonusEarned && result.bonusEarned > 0) {
        setTimeout(() => {
          addToast(result.bonusEarned!, `${result.streak}일 연속 달성 보너스`);
          toast.success(
            `${result.streak}일 연속 출석! +${result.bonusEarned}P 보너스 지급됐어요 🎉`,
          );
        }, 800);
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.point.checkinStatus });
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
      queryClient.invalidateQueries({ queryKey: queryKeys.point.me });
      queryClient.invalidateQueries({ queryKey: ['point', 'history'] });
      queryClient.invalidateQueries({ queryKey: ['attendance', 'me'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? '출석체크에 실패했어요.');
    },
  });

  // 달력 그리드 계산
  const grid = useMemo(() => {
    const firstDay = new Date(Date.UTC(year, month0, 1));
    const firstWeekday = firstDay.getUTCDay(); // 0=일
    const daysInMonth = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
    const cells: Array<number | null> = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month0]);

  const checkedSet = useMemo(() => new Set(monthData?.days ?? []), [monthData]);
  const altSet = useMemo(() => new Set(monthData?.alt_days ?? []), [monthData]);
  const replaceableDate = monthData?.replaceable_date ?? null;
  const altCountThisWeek = monthData?.alternative_count_this_week ?? 0;

  const shiftMonth = (delta: number) => {
    const next = new Date(Date.UTC(year, month0 + delta, 1));
    setYear(next.getUTCFullYear());
    setMonth0(next.getUTCMonth());
  };

  const checkedToday = status?.checkedToday ?? false;
  const streak = status?.streak ?? 0;
  const monthlyCount = checkedSet.size;

  const isTodayMonth = year === today.year && month0 === today.month0;
  const canNext = !(year === today.year && month0 === today.month0);

  return (
    <section className="mb-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
      {/* 헤더 */}
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold">출석체크</h3>
          <div className="mt-0.5 text-xs text-[var(--color-text-sub)]">
            <span className="font-semibold text-[var(--color-primary)]">
              {streak}일 연속
            </span>{' '}
            · 이번 달 <b>{monthlyCount}일</b> 출석
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => {
            if (!isLoggedIn) return setLoginModalOpen(true);
            checkinMutation.mutate();
          }}
          disabled={checkedToday || checkinMutation.isPending}
          loading={checkinMutation.isPending}
          variant={checkedToday ? 'ghost' : 'primary'}
        >
          {checkedToday ? (
            <>
              <Check className="h-4 w-4" />
              오늘 출석 완료
            </>
          ) : (
            '출석체크'
          )}
        </Button>
      </div>

      {/* 달력 네비 */}
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          className="rounded p-1 hover:bg-gray-100"
          aria-label="이전 달"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold">
          {year}년 {month0 + 1}월
        </span>
        <button
          type="button"
          onClick={() => canNext && shiftMonth(1)}
          disabled={!canNext}
          className="rounded p-1 hover:bg-gray-100 disabled:opacity-30"
          aria-label="다음 달"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* 요일 */}
      <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[11px] text-[var(--color-text-sub)]">
        {WEEKDAYS.map((w) => (
          <div key={w} className="py-1">
            {w}
          </div>
        ))}
      </div>

      {/* 일자 */}
      <div className="grid grid-cols-7 gap-1">
        {grid.map((d, i) => {
          if (d === null) return <div key={i} />;
          const dateStr = toDateStr(year, month0, d);
          const checked = checkedSet.has(dateStr);
          const isAlt = altSet.has(dateStr);
          const isReplaceable = replaceableDate === dateStr;
          const isToday = isTodayMonth && d === today.day;
          // 미래 날짜 여부: 보는 달이 이번 달이면 오늘 이후, 미래 달이면 전체 (canNext=false 라 사실상 불가)
          const isFuture =
            year > today.year ||
            (year === today.year && month0 > today.month0) ||
            (isTodayMonth && d > today.day);

          const base =
            'flex h-9 w-9 items-center justify-center rounded-full transition-colors';
          let cls: string;
          let ariaLabel: string;
          let content: string | number = d;

          if (isFuture) {
            cls = `${base} bg-transparent text-gray-300 pointer-events-none text-xs font-semibold`;
            ariaLabel = `${dateStr} (미래)`;
          } else if (isToday) {
            // 오늘 셀 — 숫자 대신 '오늘' 텍스트.
            // 출석 완료면 초록(bg-emerald-500), 미출석이면 파랑(bg-[var(--color-primary)]).
            const bg = checked ? 'bg-emerald-500' : 'bg-[var(--color-primary)]';
            cls = `${base} ${bg} text-white text-[10px] font-bold`;
            ariaLabel = checked ? `${dateStr} 오늘 출석` : `${dateStr} 오늘`;
            content = '오늘';
          } else if (isAlt) {
            // 대체 출석 — 직접 출석(초록)과 구분되는 노란색(amber)
            cls = `${base} bg-amber-400 text-white text-xs font-semibold`;
            ariaLabel = `${dateStr} 대체 출석`;
          } else if (checked) {
            cls = `${base} bg-emerald-500 text-white text-xs font-semibold`;
            ariaLabel = `${dateStr} 출석`;
          } else if (isReplaceable) {
            // 대체 가능 — 점선 테두리 + 연한 파란 배경
            cls = `${base} border-2 border-dashed border-[var(--color-primary)] bg-blue-50 text-[var(--color-primary)] text-xs font-semibold`;
            ariaLabel = `${dateStr} 대체 가능`;
          } else {
            cls = `${base} bg-gray-200 text-gray-700 text-xs font-semibold`;
            ariaLabel = `${dateStr} 미출석`;
          }
          return (
            <div key={i} className="flex items-center justify-center py-1">
              <div className={cls} aria-label={ariaLabel}>
                {content}
              </div>
            </div>
          );
        })}
      </div>

      {/* 범례 */}
      <div className="mt-2 flex flex-wrap items-center justify-end gap-3 text-[11px] text-[var(--color-text-sub)]">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-full bg-emerald-500" />
          출석
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-full bg-amber-400" />
          대체
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-full border-2 border-dashed border-[var(--color-primary)] bg-blue-50" />
          대체가능
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-full bg-gray-200" />
          미출석
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-full bg-[var(--color-primary)]" />
          오늘
        </span>
      </div>

      {/* 대체 출석 안내 — 활동으로 미출석 보완 */}
      <div className="mt-3 rounded-lg border border-dashed border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 p-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs font-semibold text-[var(--color-text)]">
              활동으로 미출석 보완하기
            </p>
            <p className="mt-1 text-[11px] leading-relaxed text-[var(--color-text-sub)]">
              게시글 작성, 댓글 3개, 매매일지 등록 시
              <br />
              가장 최근 미출석일이 자동 출석 처리돼요.
            </p>
          </div>
          <span className="shrink-0 rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 text-[10px] font-bold text-[var(--color-primary)]">
            최근 7일 {altCountThisWeek}/{ALT_WEEKLY_LIMIT}회 사용
          </span>
        </div>
        {replaceableDate && altCountThisWeek < ALT_WEEKLY_LIMIT && (
          <p className="mt-2 text-[11px] font-medium text-[var(--color-primary)]">
            💡 오늘 활동하면 {replaceableDate.slice(5, 7)}월 {replaceableDate.slice(8, 10)}일 출석이
            자동 인정돼요.
          </p>
        )}
      </div>

      {/* 연속 출석 현황 + 보너스 안내 */}
      <div className="mt-4 border-t border-[var(--color-border)] pt-3">
        {streak > 0 && (
          <p className="mb-2 text-sm font-semibold text-[var(--color-text)]">
            🔥 {streak}일 연속 출석 중!
            {status?.nextBonus && (
              <span className="ml-2 text-xs font-normal text-[var(--color-text-sub)]">
                {status.nextBonus.days}일까지 {status.nextBonus.days - streak}일 남음
              </span>
            )}
          </p>
        )}
        <div className="flex flex-wrap gap-2 text-xs text-[var(--color-text-sub)]">
          <span className="rounded-full bg-[var(--color-bg)] px-2.5 py-1">매일 +10P · +5점</span>
          <span className="rounded-full bg-[var(--color-bg)] px-2.5 py-1">매 7일 연속 +50P</span>
          <span className="rounded-full bg-[var(--color-bg)] px-2.5 py-1">매 30일 연속 +300P</span>
        </div>
      </div>
    </section>
  );
}
